<script>
	// NPM Modules
	import collect from 'collect.js';
	// Stores
	import AdminUserStore from '@/Stores/AdminUserStore.js';
	import PermissionStore from '@/Stores/PermissionStore.js';
	import RoleStore from '@/Stores/RoleStore.js';
	import ReportStore from '@/Stores/ReportStore.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import Loading from '@/Components/Loading.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import Helmet from '@/Components/Helmet.svelte';
	import { AdminUserStatus, AdminUserStatusName } from '@/Constants/AdminUser.js';

	export let id;

	$: adminUser = $AdminUserStore && $AdminUserStore.firstWhere('id', id);
	$: adminPermissions = (adminUser && adminUser.permissions) || collect([]);
	$: rolePermissions = (adminUser && adminUser.roles && adminUser.roles.pluck('permissions').first()) || collect([]);
	// eslint-disable-next-line no-unused-vars
	$: statusOptions = Object.entries(AdminUserStatus).map(([key, value]) => [value, AdminUserStatusName[value]]);

	let loading = loadDetails();
	let sendingPermissions = false;
	let sendingStatus = false;
	let tab = 'general';
	let allReportsAccess = false;

	$: allReportsAccess =
		adminUser && adminUser.report_ids && Array.isArray(adminUser.report_ids) && adminUser.report_ids.includes(0);

	$: currentRole = adminUser && adminUser.roles && adminUser.roles.pluck('id').first();
	$: currentStatus = adminUser && adminUser.status_id;

	$: role = currentRole;
	$: status = currentStatus;

	async function loadDetails() {
		await AdminUserStore.getAdminUser(id);
		await RoleStore.getRoles();
		await ReportStore.getReports();
		return PermissionStore.getPermissions();
	}

	function onSavePermissions() {
		sendingPermissions = true;
		const permission_ids = adminPermissions
			.sortBy((item) => parseInt(item))
			.pluck('id')
			.toArray();
		const report_ids = adminUser.report_ids.sort();
		AdminUserStore.patchData(adminUser.id, { permission_ids, report_ids })
			.then(() => {
				notify({
					type: 'success',
					title: adminUser.name,
					message: 'Admin User Permissions Saved',
					timeout: 3
				});
				AdminUserStore.getAdminUser(id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem updating the permissions',
					timeout: 5
				});
			})
			.finally(() => (sendingPermissions = false));
	}

	function updateRole() {
		AdminUserStore.patchData(adminUser.id, { role_ids: [role] })
			.then(() => {
				notify({
					type: 'success',
					title: adminUser.name,
					message: 'User Role Saved',
					timeout: 3
				});
				AdminUserStore.getAdminUser(id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem updating the role',
					timeout: 5
				});
			});
	}

	function updateStatus() {
		sendingStatus = true;
		AdminUserStore.patchData(adminUser.id, { status_id: status })
			.then(() => {
				notify({
					type: 'success',
					title: adminUser.name,
					message: 'User Status Saved',
					timeout: 3
				});
				AdminUserStore.getAdminUser(id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem updating user status',
					timeout: 5
				});
			})
			.finally(() => (sendingStatus = false));
	}

	function onUpdateUser() {
		if (currentStatus !== status) {
			updateStatus();
		}

		if (currentRole !== role) {
			updateRole();
		}
	}

	function onUpdateAccountManager() {
		AdminUserStore.patchData(adminUser.id, {
			is_account_manager: !adminUser.is_account_manager
		})
			.then(() => {
				notify({
					type: 'success',
					title: adminUser.name,
					message: 'Account Manager Status Saved',
					timeout: 3
				});
				AdminUserStore.getAdminUser(id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem updating Account Manager Status',
					timeout: 5
				});
			});
	}

	function updatePermission(permission, enabled) {
		let item = adminPermissions.firstWhere('id', permission.id);
		if (item && !enabled) {
			adminPermissions = adminPermissions.reject((item) => item.id === permission.id);
		} else {
			adminPermissions.push(permission);
		}
	}

	function transformName(permissionName) {
		// any name with no dot (.) something is wildcard
		return permissionName.split('.')[1] ? permissionName : `${permissionName}.*`;
	}

	const allReportsAccessToggle = (setAccess) => {
		if (setAccess) {
			adminUser.report_ids = [0];
		} else {
			adminUser.report_ids = [];
		}
	};

	const reportAccessToggled = (reportId) => {
		if (allReportsAccess) {
			adminUser.report_ids = [];
			let reportsToAdd = [];
			$ReportStore.each((report) => {
				if (parseInt(report.id) != parseInt(reportId)) {
					reportsToAdd.push(parseInt(report.id));
				}
			});
			adminUser.report_ids = reportsToAdd;
		} else {
			if (adminUser.report_ids.includes(parseInt(reportId))) {
				adminUser.report_ids = adminUser.report_ids.filter(function (value) {
					return value != parseInt(reportId);
				});
			} else {
				adminUser.report_ids.push(parseInt(reportId));
				adminUser.report_ids = adminUser.report_ids;
			}
		}
	};
</script>

<Helmet title={'Admin user - ' + adminUser?.full_name} />

<div>
	{#await loading}
		<Loading />
	{:then}
		{#if adminUser && adminPermissions}
			<div>
				<!-- Page Header -->
				<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
					<div class="flex items-center w-full">
						<a
							href={'/admin-users'}
							title="Admin Users List"
							class="flex cursor-pointer hover:underline text-grey items-center"
						>
							<Icon name="chevron-left" classes="w-2 mr-1" />
							<div class="mr-6 text-xl font-bold">Admin Users List</div>
						</a>
						<h1 class="flex-1 text-xl font-bold">{adminUser.name}</h1>
						<div class="flex items-center pr-4 mr-8 space-x-2 font-semibold uppercase border-r-2">
							<div>Account Manager</div>
							<CheckBox checked={adminUser.is_account_manager} on:change={onUpdateAccountManager} />
						</div>
						{#if !adminUser.is_super_user}
							<form on:submit|preventDefault={onUpdateUser} class="flex items-center mr-2">
								<div class="mr-2 font-semibold uppercase">Role</div>
								<SelectInput className="w-full lg:pr-4" name="role" required={true} bind:value={role}>
									<option>Select Role...</option>
									{#each $RoleStore.all() as availableRole (availableRole.id)}
										{#if parseInt(availableRole.id) === parseInt(role)}
											<option value={availableRole.id} selected>{availableRole.name}</option>
										{:else}
											<option value={availableRole.id}>{availableRole.name}</option>
										{/if}
									{/each}
								</SelectInput>

								<div class="mr-2 font-semibold uppercase">Status</div>
								<SelectInput className="w-full lg:pr-4" name="role" required={true} bind:value={status}>
									<option>Update Status...</option>
									{#each statusOptions as availableStatus}
										{#if parseInt(availableStatus[0]) === parseInt(status)}
											<option value={availableStatus[0]} selected>{availableStatus[1]}</option>
										{:else}
											<option value={availableStatus[0]}>{availableStatus[1]}</option>
										{/if}
									{/each}
								</SelectInput>
								<LoadingButton type="submit" loading={sendingStatus} className="btn btn-black">Update</LoadingButton>
							</form>
						{/if}
					</div>
				</div>
				<!-- /Page Header -->
				<div class="p-2 mx-4 bg-gray-100 rounded">
					<div class="mt-2 rounded">
						<div class="flex items-center justify-between">
							<h2 class="w-full font-semibold text-center uppercase">
								{adminUser.name} Permissions
							</h2>
							{#if !adminUser.is_super_user}
								<LoadingButton
									on:click={onSavePermissions}
									type="button"
									loading={sendingPermissions}
									className="btn btn-black">Save</LoadingButton
								>
							{/if}
						</div>
						{#if !adminUser.is_super_user}
							<div class="flex items-center justify-between my-2">
								<div class="flex items-center justify-around w-full">
									<div class="flex-1 text-center">
										<div class="btn-group">
											<button
												class="btn {tab === 'general' || tab === null || tab === undefined
													? 'btn-primary-action'
													: 'btn-muted text-dark'}"
												on:click={() => {
													tab = 'general';
												}}
											>
												General Permissions
											</button>
											<button
												class="btn {tab === 'report' ? 'btn-primary-action' : 'btn-muted text-dark'}"
												on:click={() => {
													tab = 'report';
												}}
											>
												Report Permissions
											</button>
										</div>
									</div>
								</div>
							</div>
							{#if tab === 'general'}
								<table class="w-full whitespace-nowrap">
									<thead>
										<tr class="text-xs text-left text-gray-500 uppercase">
											<th class="px-6 pt-5 pb-4 font-medium">Permission</th>
											<th class="px-6 pt-5 pb-4 font-medium">
												{(adminUser.roles && adminUser.roles.pluck('name').first()) || 'NO ROLE'}
											</th>
											<th class="px-6 pt-5 pb-4 font-medium text-center">User</th>
										</tr>
									</thead>
									<tbody class="bg-white">
										{#each $PermissionStore.all() as permission (permission.id)}
											<tr class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
												<td class="w-full p-4 px-6 border-t">
													{transformName(permission.name)}
												</td>
												<td class="p-4 px-6 border-t">
													<CheckBox disabled={true} checked={rolePermissions.firstWhere('id', permission.id)} />
												</td>
												<td class="p-4 px-6 border-t">
													<CheckBox
														on:change={(e) => updatePermission(permission, e.srcElement.checked)}
														checked={adminPermissions.firstWhere('id', permission.id)}
													/>
												</td>
											</tr>
										{:else}
											<tr>
												<td class="px-6 py-4 text-center border-t" colspan="3"> No permissions found. </td>
											</tr>
										{/each}
									</tbody>
								</table>
							{:else if tab === 'report'}
								<div class="flex justify-end">
									<div class="font-semibold flex flex-row space-x-3 mr-6 items-center">
										<span>Allow all reports</span>
										<CheckBox
											checked={allReportsAccess}
											on:change={(e) => {
												allReportsAccessToggle(e.target.checked);
											}}
										/>
									</div>
								</div>
								<table class="w-full whitespace-nowrap">
									<thead>
										<tr class="text-xs text-left text-gray-500 uppercase">
											<th class="px-6 pt-5 pb-4 font-medium">Description</th>
											<th class="w-px px-6 pt-5 pb-4 font-medium text-center">User</th>
										</tr>
									</thead>
									<tbody class="bg-white">
										{#each adminUser && $ReportStore.all() as report (report.id)}
											<tr class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
												<td class="p-4 px-6 font-semibold border-t">
													{report.name}
												</td>
												<td class="p-4 px-6 border-t">
													<CheckBox
														checked={adminUser &&
															adminUser.report_ids &&
															(adminUser.report_ids.includes(parseInt(report.id)) || adminUser.report_ids.includes(0))}
														on:change={() => {
															reportAccessToggled(report.id);
														}}
													/>
												</td>
											</tr>
										{:else}
											<tr>
												<td class="px-6 py-4 text-center border-t" colspan="2"> No reports found. </td>
											</tr>
										{/each}
									</tbody>
								</table>
							{/if}
						{:else}
							<div class="py-6 mt-2 text-center text-red-500 bg-white">Super User Permissions to Everything</div>
						{/if}
					</div>
				</div>
			</div>
		{/if}
	{/await}
</div>
